/**
 * Scout MVP — Skrzynka Scouta: materiały ze statusami.
 * Do akceptacji → (Akceptuj) → Do wysłania → (Oznacz jako wysłane) → Historia. Kosz z przywróceniem.
 * PDF dostępny dopiero po akceptacji.
 */
import { useEffect, useState } from 'react';
import { API } from '@/utils/api';
import {
  Inbox, Check, Copy, Trash2, RotateCcw, Send, FileDown, Pencil, Loader2, X, MessageCircleQuestion,
} from 'lucide-react';
import { TYPE_META } from '@/pages/ScoutStudio';

const panel = 'rounded-2xl border border-[var(--sc-border)] bg-[var(--sc-surface)]';
const chip = 'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold';

const TABS = [
  { id: 'do_akceptacji', label: 'Do akceptacji' },
  { id: 'do_wyslania', label: 'Do wysłania' },
  { id: 'historia', label: 'Historia' },
  { id: 'kosz', label: 'Kosz' },
];

export default function ScoutSkrzynka({ client, onNavigate }) {
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState('do_akceptacji');

  const load = () => {
    fetch(`${API}/scout/inbox/${client.id}`)
      .then((r) => r.json())
      .then((d) => setItems(d?.ok ? d.items : []))
      .catch(() => setItems([]));
  };
  useEffect(() => { setItems(null); setTab('do_akceptacji'); load(); }, [client.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (id, action) => {
    try {
      const res = await fetch(`${API}/scout/inbox/status`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, action }),
      });
      const d = await res.json();
      if (d?.ok) setItems((list) => list.map((it) => (it.id === id ? { ...it, status: d.status } : it)));
    } catch { /* noop */ }
  };

  const saveEdit = async (id, title, content) => {
    try {
      const res = await fetch(`${API}/scout/inbox/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, content }),
      });
      const d = await res.json();
      if (d?.ok) setItems((list) => list.map((it) => (it.id === id ? d.item : it)));
      return d?.ok;
    } catch { return false; }
  };

  const counts = TABS.reduce((acc, t) => ({ ...acc, [t.id]: (items || []).filter((i) => i.status === t.id).length }), {});
  const visible = (items || []).filter((i) => i.status === tab);

  return (
    <div className="space-y-5" data-testid="view-skrzynka">
      <div>
        <h2 className="text-lg font-bold flex items-center gap-2"><Inbox className="w-5 h-5 text-[#FF8A3D]" /> Skrzynka Scouta</h2>
        <p className="text-xs text-[var(--sc-t45)] mt-0.5 ml-7">Wszystko, co przygotował Scout. Akceptujesz, poprawiasz, kopiujesz albo pobierasz PDF.</p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="skrzynka-tabs">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`skrzynka-tab-${t.id}`}
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition border ${tab === t.id ? 'bg-[#F26A21] border-transparent text-white' : 'border-[var(--sc-border2)] text-[var(--sc-t50)] hover:text-[var(--sc-t100)] hover:bg-[var(--sc-hover)]'}`}>
            {t.label}
            <span className={`text-[10px] font-extrabold rounded-full px-1.5 py-0.5 ${tab === t.id ? 'bg-white/20' : 'bg-[var(--sc-surface3)]'}`}>{counts[t.id] || 0}</span>
          </button>
        ))}
      </div>

      {items === null && <div className={`${panel} p-8 text-center`}><Loader2 className="w-5 h-5 mx-auto animate-spin text-[#FF8A3D]" /></div>}

      {items !== null && visible.length === 0 && (
        <div className={`${panel} p-8 text-center`} data-testid="skrzynka-empty">
          <Inbox className="w-10 h-10 mx-auto text-[var(--sc-t15)] mb-3" />
          <p className="text-sm text-[var(--sc-t50)]">
            {tab === 'do_akceptacji'
              ? 'Nic nie czeka na akceptację. Materiały pojawią się tu po rozmowie ze Scoutem.'
              : 'Ta półka jest pusta.'}
          </p>
          {tab === 'do_akceptacji' && (
            <button onClick={() => onNavigate?.('zapytaj')} data-testid="skrzynka-goto-dialog"
              className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#F26A21] hover:bg-[#FF7C33] px-4 py-2.5 text-sm font-bold text-white transition">
              <MessageCircleQuestion className="w-4 h-4" /> Zacznij rozmowę
            </button>
          )}
        </div>
      )}

      <div className="space-y-3">
        {visible.map((it) => <InboxCard key={it.id} it={it} onAct={act} onSaveEdit={saveEdit} />)}
      </div>
    </div>
  );
}

function InboxCard({ it, onAct, onSaveEdit }) {
  const meta = TYPE_META[it.type] || TYPE_META.inne;
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(it.title);
  const [content, setContent] = useState(it.content);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);

  const copy = async () => {
    try { await navigator.clipboard.writeText(it.content); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* noop */ }
  };
  const save = async () => {
    setSaving(true);
    const ok = await onSaveEdit(it.id, title, content);
    setSaving(false);
    if (ok) setEditing(false);
  };

  const date = (it.updated_at || it.created_at || '').slice(0, 10);
  return (
    <div className={`${panel} p-4`} data-testid={`inbox-item-${it.id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <span className={chip} style={{ background: `${meta.color}22`, color: meta.color }}>{meta.label}</span>
        {editing ? (
          <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid={`inbox-edit-title-${it.id}`}
            className="flex-1 min-w-[200px] rounded-lg bg-[var(--sc-input)] border border-[var(--sc-border2)] px-3 py-1.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-orange-400/50" />
        ) : (
          <button onClick={() => setExpanded(!expanded)} className="flex-1 min-w-[200px] text-left text-sm font-semibold hover:text-orange-300 transition truncate" data-testid={`inbox-title-${it.id}`}>
            {it.title}
          </button>
        )}
        <span className="text-[10px] text-[var(--sc-t35)]">{it.channel ? `${it.channel} · ` : ''}{date}</span>
      </div>

      {(expanded || editing) && (
        editing ? (
          <textarea value={content} onChange={(e) => setContent(e.target.value)} data-testid={`inbox-edit-content-${it.id}`}
            className="mt-3 w-full min-h-[140px] rounded-xl bg-[var(--sc-input)] border border-[var(--sc-border2)] px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400/50" />
        ) : (
          <p className="mt-3 text-sm text-[var(--sc-t75,var(--sc-t70))] leading-relaxed whitespace-pre-wrap rounded-xl bg-[var(--sc-surface2)] border border-[var(--sc-border)] p-3.5" data-testid={`inbox-content-${it.id}`}>{it.content}</p>
        )
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {editing ? (
          <>
            <ActionBtn onClick={save} primary testid={`inbox-save-${it.id}`}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Zapisz zmiany</ActionBtn>
            <ActionBtn onClick={() => { setEditing(false); setTitle(it.title); setContent(it.content); }} testid={`inbox-cancel-edit-${it.id}`}><X className="w-4 h-4" /> Anuluj</ActionBtn>
          </>
        ) : (
          <>
            {it.status === 'do_akceptacji' && (
              <>
                <ActionBtn onClick={() => onAct(it.id, 'akceptuj')} primary testid={`inbox-accept-${it.id}`}><Check className="w-4 h-4" /> Akceptuj</ActionBtn>
                <ActionBtn onClick={() => { setEditing(true); setExpanded(true); }} testid={`inbox-edit-${it.id}`}><Pencil className="w-4 h-4" /> Edytuj</ActionBtn>
                <ActionBtn onClick={copy} testid={`inbox-copy-${it.id}`}>{copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? 'Skopiowano' : 'Kopiuj'}</ActionBtn>
                <ActionBtn onClick={() => onAct(it.id, 'kosz')} danger testid={`inbox-trash-${it.id}`}><Trash2 className="w-4 h-4" /></ActionBtn>
              </>
            )}
            {it.status === 'do_wyslania' && (
              <>
                <ActionBtn onClick={copy} primary testid={`inbox-copy-${it.id}`}>{copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? 'Skopiowano' : 'Kopiuj'}</ActionBtn>
                <a href={`${API}/scout/inbox/item/${it.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`inbox-pdf-${it.id}`}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--sc-border2)] px-3 py-2 text-sm font-semibold text-[var(--sc-t60)] hover:text-[var(--sc-t100)] hover:bg-[var(--sc-hover)] transition">
                  <FileDown className="w-4 h-4" /> Pobierz PDF
                </a>
                <ActionBtn onClick={() => onAct(it.id, 'wyslane')} testid={`inbox-sent-${it.id}`}><Send className="w-4 h-4" /> Oznacz jako wysłane / opublikowane</ActionBtn>
                <ActionBtn onClick={() => onAct(it.id, 'kosz')} danger testid={`inbox-trash-${it.id}`}><Trash2 className="w-4 h-4" /></ActionBtn>
              </>
            )}
            {it.status === 'historia' && (
              <>
                <ActionBtn onClick={copy} testid={`inbox-copy-${it.id}`}>{copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? 'Skopiowano' : 'Kopiuj'}</ActionBtn>
                <a href={`${API}/scout/inbox/item/${it.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`inbox-pdf-${it.id}`}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--sc-border2)] px-3 py-2 text-sm font-semibold text-[var(--sc-t60)] hover:text-[var(--sc-t100)] hover:bg-[var(--sc-hover)] transition">
                  <FileDown className="w-4 h-4" /> PDF
                </a>
              </>
            )}
            {it.status === 'kosz' && (
              <ActionBtn onClick={() => onAct(it.id, 'przywroc')} testid={`inbox-restore-${it.id}`}><RotateCcw className="w-4 h-4" /> Przywróć</ActionBtn>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ActionBtn({ children, onClick, primary, danger, testid }) {
  const cls = primary
    ? 'bg-[#F26A21] hover:bg-[#FF7C33] text-white'
    : danger
      ? 'border border-[var(--sc-border2)] text-[var(--sc-t40)] hover:text-red-400 hover:border-red-400/40'
      : 'border border-[var(--sc-border2)] text-[var(--sc-t60)] hover:text-[var(--sc-t100)] hover:bg-[var(--sc-hover)]';
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition ${cls}`}>
      {children}
    </button>
  );
}
